import { Plus, Search, Loader2 } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { comercialService } from "@/services/modules/comercial";
import { cadastrosService, type CadastroClient } from "@/services/modules/cadastros";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

type Props = {
  open: boolean;
  onClose: () => void;
  moduleId: string;
  moduleLabel: string;
};

type ModuleField = {
  label: string;
  placeholder: string;
};

type ModuleConfig = {
  fields: ModuleField[];
  primaryActionLabel?: string;
  titleFieldLabel?: string;
};

const moduleConfigs: Record<string, ModuleConfig> = {
  comercial: {
    primaryActionLabel: "Salvar e Criar Atendimento",
    fields: [
      { label: "Serviço de Interesse", placeholder: "Ex.: Depósito de Marca" },
      { label: "Canal de Origem", placeholder: "Ex.: Indicação, Inbound..." },
      { label: "Valor Previsto", placeholder: "Ex.: R$ 5.000,00" },
    ],
  },
  marcas: {
    titleFieldLabel: "Nome da Marca",
    fields: [
      { label: "Número do Processo", placeholder: "Ex.: 927371234" },
      { label: "Nome da Marca", placeholder: "Ex.: XPTO TECH" },
      { label: "Classe NCL", placeholder: "Ex.: 42" },
    ],
  },
  patentes: {
    fields: [
      { label: "Título da Invenção", placeholder: "Ex.: Sistema de filtragem..." },
      { label: "CPC", placeholder: "Ex.: B01D" },
      { label: "Inventor(es)", placeholder: "Nome(s) do(s) inventor(es)" },
    ],
  },
  di: {
    fields: [
      { label: "Título do Desenho", placeholder: "Ex.: Embalagem para cosmético" },
      { label: "Categoria Locarno", placeholder: "Ex.: 09-01" },
      { label: "Referência Interna", placeholder: "Ex.: DI-2026-001" },
    ],
  },
  internacional: {
    fields: [
      { label: "Título", placeholder: "Ex.: Marca ou Patente internacional" },
      { label: "País(es)", placeholder: "Ex.: EUA, EP, JP" },
      { label: "Protocolo Internacional", placeholder: "Nº de protocolo" },
    ],
  },
  software: {
    fields: [
      { label: "Título do Software", placeholder: "Ex.: Plataforma de gestão IP" },
      { label: "Linguagem/Stack", placeholder: "Ex.: TypeScript, Python..." },
      { label: "Repositório/Referência", placeholder: "Ex.: SW-2026-001" },
    ],
  },
  financeiro: {
    fields: [
      { label: "Cliente", placeholder: "Ex.: Grupo Pharma S.A." },
      { label: "Descrição do Recebimento", placeholder: "Ex.: Parcela de demanda" },
      { label: "Valor", placeholder: "Ex.: R$ 2.450,00" },
    ],
  },
  cadastros: {
    fields: [
      { label: "Razão Social", placeholder: "Ex.: Empresa XPTO Ltda." },
      { label: "CNPJ/CPF", placeholder: "Ex.: 00.000.000/0001-00" },
      { label: "Contato Principal", placeholder: "Ex.: Nome e e-mail" },
    ],
  },
};

export function NovoProcessoModal({ open, onClose, moduleId, moduleLabel }: Props) {
  const [activeTab, setActiveTab] = useState<"processo" | "monitoramento">("processo");
  const [clientSearch, setClientSearch] = useState("");
  const [submittedClientSearch, setSubmittedClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<CadastroClient | null>(null);
  
  // Form field values
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  const moduleConfig = moduleConfigs[moduleId];
  const fields = moduleConfig?.fields || [];
  const primaryActionLabel = moduleConfig?.primaryActionLabel || "Salvar e Criar Processo";
  const titleFieldLabel = moduleConfig?.titleFieldLabel || fields[0]?.label;
  const isComercialModule = moduleId === "comercial";
  const isTrademarkModule = moduleId === "marcas";
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();

  const clientsQuery = useQuery({
    queryKey: ["novo-processo-clients", submittedClientSearch],
    queryFn: () => cadastrosService.getClients({ q: submittedClientSearch || undefined }),
    enabled: open,
    staleTime: 1000 * 60 * 5,
  });

  const clients = clientsQuery.data || [];

  function normalizeOpportunityDomain(value: string) {
    if (value === "marcas" || value === "patentes" || value === "di" || value === "internacional") {
      return value;
    }

    return undefined;
  }

  function resetForm() {
    setFieldValues({});
    setSelectedClient(null);
    setClientSearch("");
    setSubmittedClientSearch("");
    setActiveTab("processo");
  }

  function buildOpportunityMetadata() {
    const metadata: Record<string, string> = {
      source_module_id: moduleId,
      source_module_label: moduleLabel,
    };

    for (const field of fields) {
      const value = fieldValues[field.label]?.trim();
      if (!value) continue;
      metadata[field.label] = value;
    }

    if (isTrademarkModule) {
      const processNumber = fieldValues["Número do Processo"]?.trim();
      if (processNumber) {
        metadata.process_number = processNumber;
      }
    }

    return metadata;
  }

  function handleSubmit() {
    if (isTrademarkModule && !fieldValues["Número do Processo"]?.trim()) {
      toast({
        title: "Número do processo obrigatório",
        description: "Informe o número do processo do NIP TM antes de criar o atendimento.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedClient) {
      toast({
        title: "Cliente obrigatório",
        description: "Selecione um cliente antes de salvar.",
        variant: "destructive",
      });
      return;
    }

    createMutation.mutate();
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      // Map the generic fields to the CRM opportunity payload
      const title = (titleFieldLabel ? fieldValues[titleFieldLabel] : "") || fieldValues[fields[0]?.label] || `Novo Processo ${moduleLabel}`;
      const value_str = fieldValues["Valor Previsto"] || fieldValues[fields[2]?.label] || "";
      const value_estimate = parseFloat(value_str.replace(/[^0-9.,]/g, "").replace(",", ".")) || undefined;
      const metadata = buildOpportunityMetadata();

      return comercialService.createOportunidade({
        title,
        stage: "lead",
        value_estimate,
        clientId: selectedClient.id,
        domain: normalizeOpportunityDomain(moduleId),
        metadata,
      });
    },
    onSuccess: () => {
      toast({
        title: isComercialModule ? "Atendimento criado com sucesso!" : "Processo criado com sucesso!",
        description: isComercialModule ? `O atendimento foi criado no CRM para ${moduleLabel}.` : `Módulo: ${moduleLabel}`,
      });
      queryClient.invalidateQueries({ queryKey: ["comercial"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard", "comercial"] });
      resetForm();
      onClose();
    },
    onError: () => {
      toast({
        title: isComercialModule ? "Erro ao criar atendimento" : "Erro ao criar processo",
        description: isComercialModule ? "Não foi possível criar o atendimento. Tente novamente." : "Tente novamente.",
        variant: "destructive",
      });
    },
  });

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          resetForm();
          onClose();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-hidden rounded-xl border bg-white p-0 flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 bg-slate-50/50 dark:bg-slate-900/50">
          <DialogTitle className="text-base font-bold text-slate-800">{isComercialModule ? "Novo Atendimento" : "Novo Processo"} — {moduleLabel}</DialogTitle>
          <DialogDescription className="sr-only">
            {isComercialModule ? "Cadastre um novo atendimento no CRM selecionando o cliente e os dados do serviço." : "Cadastre um novo processo selecionando o cliente e os dados do pedido."}
          </DialogDescription>
        </div>

        {/* Global Tabs */}
        <div className="flex border-b border-slate-200 px-5">
          <button
            onClick={() => setActiveTab("processo")}
            className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "processo" ? "border-primary text-primary" : "border-transparent text-slate-400 hover:text-slate-800"
            }`}
          >
            Dados do Processo
          </button>
          <button
            onClick={() => setActiveTab("monitoramento")}
            className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "monitoramento" ? "border-primary text-primary" : "border-transparent text-slate-400 hover:text-slate-800"
            }`}
          >
            Monitoramento
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === "processo" && (
            <div className="space-y-5 animate-fade-in">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-800">Lead / Cliente</label>
                {selectedClient ? (
                  <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 border border-slate-200">
                    <span className="text-xs font-medium text-slate-800">
                      {selectedClient.name}
                      {selectedClient.document ? <span className="ml-1 text-slate-400">• {selectedClient.document}</span> : null}
                    </span>
                    <button onClick={() => setSelectedClient(null)} className="text-[10px] font-semibold text-primary hover:underline">
                      Alterar
                    </button>
                  </div>
                ) : (
                  <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/30 dark:bg-slate-900/30">
                    <div className="mb-2 flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          value={clientSearch}
                          onChange={(event) => setClientSearch(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              setSubmittedClientSearch(clientSearch.trim());
                            }
                          }}
                          placeholder="Buscar por nome, CPF/CNPJ..."
                          className="h-9 w-full rounded-md border border-slate-200 bg-background pl-9 pr-3 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-primary/30"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setSubmittedClientSearch(clientSearch.trim())}
                        className="inline-flex h-9 items-center gap-1 rounded-md border border-slate-200 px-3 text-xs font-medium text-slate-700 transition-colors hover:bg-muted"
                      >
                        {clientsQuery.isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                        Buscar
                      </button>
                    </div>
                    <div className="max-h-32 space-y-1 overflow-y-auto pr-1">
                      {clientsQuery.isLoading ? (
                        <div className="flex items-center justify-center gap-2 rounded-md px-3 py-4 text-xs text-slate-500">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Carregando clientes...
                        </div>
                      ) : null}
                      {!clientsQuery.isLoading && clients.length === 0 ? (
                        <div className="rounded-md border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-slate-500">
                          Nenhum cliente encontrado.
                        </div>
                      ) : null}
                      {clients.map((client) => (
                        <button
                          key={client.id}
                          onClick={() => setSelectedClient(client)}
                          className="w-full rounded-md px-3 py-2 text-left text-xs text-slate-800 transition-colors hover:bg-muted"
                        >
                          <span className="font-medium">{client.name}</span>
                          <span className="ml-1 text-slate-400">#{client.id}</span>
                          {client.document ? <span className="ml-1 text-slate-400">• {client.document}</span> : null}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          resetForm();
                          onClose();
                          navigate("/cadastros");
                        }}
                        className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-primary/30 px-3 py-2 text-xs font-medium text-primary transition-colors hover:bg-primary/5"
                      >
                        <Plus className="h-3.5 w-3.5" /> Cadastrar novo cliente
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fields.map((field, index) => {
                  const fieldId = `novo-processo-${moduleId}-${index}`;

                  return (
                  <div key={field.label} className={fields.length % 2 !== 0 && field === fields[fields.length - 1] ? "md:col-span-2" : ""}>
                    <label htmlFor={fieldId} className="mb-1.5 block text-xs font-semibold text-slate-800">{field.label}</label>
                    <input
                      id={fieldId}
                      type="text"
                      placeholder={field.placeholder}
                      value={fieldValues[field.label] || ""}
                      onChange={(e) => setFieldValues(prev => ({ ...prev, [field.label]: e.target.value }))}
                      className="h-9 w-full rounded-md border border-slate-200 bg-background px-3 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-primary/30"
                    />
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === "monitoramento" && (
            <div className="space-y-4 animate-fade-in rounded-xl border border-amber-200 bg-amber-50/70 p-4">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-amber-900">Configuração de monitoramento movida para o módulo operacional</h3>
                <p className="text-xs text-amber-800">
                  Para evitar salvar parâmetros que não eram persistidos neste modal, a configuração detalhada de processo, colidência e mercado agora deve ser feita nas telas reais de monitoramento.
                </p>
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    onClose();
                    navigate("/operations/collisions");
                  }}
                  className="rounded-md border border-amber-300 bg-white px-3 py-2 text-left text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100"
                >
                  Abrir Monitor de Colidência
                </button>
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    onClose();
                    navigate("/monitoring/grupos");
                  }}
                  className="rounded-md border border-amber-300 bg-white px-3 py-2 text-left text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100"
                >
                  Abrir Grupos de Monitoramento
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-5 py-4 bg-slate-50/50 dark:bg-slate-900/50">
          <button onClick={onClose} className="h-9 rounded-md px-4 text-xs font-medium text-slate-400 transition-colors hover:bg-muted hover:text-slate-800">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="h-9 rounded-md bg-primary px-5 text-xs font-bold text-primary-foreground shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {createMutation.isPending ? "Salvando..." : primaryActionLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
